import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BookOpen, Sparkles, ListChecks, ClipboardList, ChevronDown, ChevronRight, UserCheck } from "lucide-react";
import { cn } from "../lib/utils";
import TrainingSessions from "../components/TrainingSessions";
import CoachDrillBankPanel from "../components/CoachDrillBankPanel";
import { useAuth } from "../context/AuthContext";
import { fetchAssignedGuides, fetchPublishedGuides } from "../lib/guides";
import { fetchAssignedDrills } from "../lib/drills";
import AssignedDrillsList from "../components/AssignedDrillsList";
import { markSurfaceNotificationsRead } from "../lib/notificationsRead";
import LoadFailureNote from "../components/LoadFailureNote";
import { coachPlayersName } from "../lib/coachPlayersName";
import LearnChrome, { useLearnNewLook } from "../components/LearnChrome";

const NAVY = "#0B2E59", GOLD = "#D4A017", GREEN = "#10B981";

// Stable id fragment for a card's disclosure trigger/panel pair — scoped by
// section key so titles only need to be unique within a section, not across
// the whole TOPICS map.
export const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** A date for the "Assigned by X · <when>" line (#382). Returns "" for a
 * value that isn't a date, so a bad timestamp costs the date, not the
 * attribution — the name is the half that matters. */
export function fmtAssignedWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

// Static reference content — no AI generation, no library fetch (see issue #6).
export const TOPICS = {
  batting: {
    label: "Batting",
    emoji: "🏏",
    color: NAVY,
    tagline: "Fundamentals for every shot in the book.",
    cards: [
      {
        title: "Ball selection",
        purpose: "Decide early, commit late — pick the right ball to attack and leave everything else.",
        points: ["Watch the release, not the batter's guess", "Play the line, not the man", "Leave with soft hands outside off"],
      },
      {
        title: "Footwork",
        purpose: "Get your head and weight moving into position before the ball arrives.",
        points: ["Trigger movement starts before release", "Front-foot press for length, back-foot for short", "Balance beats bat speed"],
      },
      {
        title: "Playing spin",
        purpose: "Use the crease and read length early to counter turn.",
        points: ["Use the depth of the crease", "Watch the seam position, not just the hand", "Play the ball, not the turn you expect"],
      },
    ],
  },
  bowling: {
    label: "Bowling",
    emoji: "🎳",
    color: GOLD,
    tagline: "Grip, approach, release, follow-through.",
    cards: [
      {
        title: "Line and length",
        purpose: "Consistency in areas beats occasional brilliance.",
        points: ["Top of off stump is a good default target", "Adjust length to the pitch, not the scoreboard", "Repeatable action beats variation without control"],
      },
      {
        title: "Building pressure",
        purpose: "String dot balls together to force a mistake.",
        points: ["Bowl to your field", "Change pace before you change line", "Set a trap over two or three overs, not one ball"],
      },
      {
        title: "Death bowling",
        purpose: "Execute a plan under scoreboard pressure.",
        points: ["Yorkers need a clear pre-set target", "Wide yorkers are safer than full tosses", "Communicate the plan with the captain before the over"],
      },
    ],
  },
  fielding: {
    label: "Fielding",
    emoji: "🧤",
    color: "#0EA5E9",
    tagline: "Ground fielding, catching and throwing technique.",
    cards: [
      {
        title: "Ground fielding",
        purpose: "Get low, get behind the line, attack the ball.",
        points: ["Get in line early, not at the last second", "Soft hands absorb pace", "Backing up saves overthrows"],
      },
      {
        title: "Catching",
        purpose: "Watch it all the way into your hands.",
        points: ["Relax the hands, don't snatch", "Move your feet to the ball's line", "Call loudly and early to avoid collisions"],
      },
      {
        title: "Throwing",
        purpose: "Hit the stumps or the keeper's gloves, every time.",
        points: ["Gather cleanly before you throw", "Low, flat throws over the top when possible", "Practice the release under fatigue, not just at full effort"],
      },
    ],
  },
  // #585 (Martin, 2026-09-02): Wicketkeeping gets its own tab, beside
  // Fielding, re-mapped off the fielding surface it folded into under #390.
  // Same posture as Fitness/Nutrition/Mindset: NO hand-curated topic cards —
  // the tab's content is the published guide group below (the ten
  // wicketkeeping guides, `surface: "wicketkeeping"`), every entry of which
  // carries a named sign-off. Authoring fresh keeping bullets here would be
  // the unreviewed-advice path the review gate exists to close.
  wicketkeeping: {
    label: "Wicketkeeping",
    emoji: "🧤",
    color: "#0D9488",
    tagline: "Standing up, standing back, glovework and stumping — reviewed guides only.",
    cards: [],
  },
  // #105 (Martin, 2026-09-02): "Field placement gets its own trigger on
  // Training & Guides, not a group under Fielding." Same template as #514
  // (Nutrition), #503 (Mindset) and #585 (Wicketkeeping), and the same
  // posture: NO hand-curated topic cards. The tab's content is the ten
  // published field-placement guides, each of which carries a reviewed
  // field diagram and the grade disclaimer on its own page.
  //
  // The guides were HELD by the importer from #98 until this slice, so
  // nothing moves off the Fielding tab — it keeps its 13 producers and its
  // reader. This is #514's shape, not #503's: nothing is removed.
  "field-placement": {
    label: "Field placement",
    emoji: "🗺️",
    color: "#0F766E",
    tagline: "Setting a field, phase by phase — reviewed guides only.",
    cards: [],
  },
  // #391: the fourth advertised tile. NO hand-curated topic cards,
  // deliberately — authoring fresh fitness/nutrition bullets here would be
  // exactly the unreviewed-advice path this slice's review gate exists to
  // close. The tab's content is the published guide groups below, each of
  // which reached a player only through a named sign-off.
  // The tagline said "…drills and fuelling" until #514: nutrition has its
  // own tab now, and a promise of fuelling here would point at the wrong tab.
  fitness: {
    label: "Fitness",
    emoji: "💪",
    color: "#16A34A",
    tagline: "Strength, conditioning and drills — reviewed guides only.",
    cards: [],
  },
  // #514 (Martin, 2026-08-27): "Nutrition should have its own tab." The
  // eight nutrition guides move off the Fitness tab (#391's fold, revisited
  // once Martin saw the combined tab live) onto their own surface. Same
  // posture as Fitness and Mindset: NO hand-curated topic cards — the tab's
  // content is the published guide group below, every entry of which carries
  // a named sign-off and the category disclosure (#501) on its own page.
  nutrition: {
    label: "Nutrition",
    emoji: "🥗",
    color: "#EA580C",
    tagline: "Fuelling, hydration and recovery — reviewed guides only.",
    cards: [],
  },
  // #503 (Andreas, 2026-08-27): Mindset gets its own tab, re-mapped off the
  // never-built "reflections" surface that left twelve published guides with
  // no list anywhere in the app. Same posture as Fitness above: NO
  // hand-curated topic cards — the tab's content is the published guide
  // group below, every entry of which carries a named sign-off and the
  // category disclosure (#501) on its own page.
  mindset: {
    label: "Mindset",
    emoji: "🧠",
    color: "#7C3AED",
    tagline: "Confidence, focus and pressure — reviewed guides only.",
    cards: [],
  },
};

/**
 * Level-2 guide groups (#390, epic #315).
 *
 * A tab shows every published guide the API assigned to its surface, grouped
 * by category so the eight `facing-bowling` guides read as their own thing
 * under Batting instead of being mixed into the twelve shot guides. Order is
 * the order groups appear down the tab; a group with nothing published in it
 * renders nothing at all, so this list is safe to extend ahead of content.
 *
 * The category → tab mapping is NOT here — it is the `surface` the API sends
 * with each guide. This array only names and orders the groups.
 *
 * `field-placement` was listed here inert from #390, on the expectation that
 * #105 would import it onto the FIELDING surface. It did not: Martin gave it
 * its own tab (2026-09-02), so its guides carry `surface: "field-placement"`
 * and this entry now labels the group on that tab instead. The entry did not
 * have to change — which is the property this array was built for.
 * `wicketkeeping` was absent for the same reason until #390 — its ten source
 * documents finally arrived, were converted and imported, and carry
 * `surface: "fielding"`, so they group under Fielding beside the thirteen
 * fielding guides. Adding the entry is the whole frontend change: the filter
 * below already reads the API's `surface`.
 */
export const GUIDE_GROUPS = [
  { category: "batting", label: "Shot guides" },
  { category: "facing-bowling", label: "Facing bowling guides" },
  { category: "bowling", label: "Bowling guides" },
  { category: "fielding", label: "Fielding guides" },
  { category: "wicketkeeping", label: "Wicketkeeping guides" },
  { category: "field-placement", label: "Field placement guides" },
  // #391: one category, two guide types — the split into guides vs drills
  // is drawn by each guide's own `guide_type` below, not by category, so
  // this list stays a naming-and-ordering concern. The "& drills" in the
  // label is about those two guide types, so it survives #514 moving
  // nutrition off this tab.
  { category: "fitness", label: "Fitness guides & drills" },
  // #514: the eight nutrition guides, on their own tab (`surface:
  // "nutrition"`) — no longer a group under Fitness.
  { category: "nutrition", label: "Nutrition guides" },
  // #503: the twelve mindset guides, on their own tab (`surface: "mindset"`).
  { category: "mindset", label: "Mindset guides" },
];

// Issue #218: the tab a bare `/learn` lands on, and the tab that comes
// first in the row below. Both moved from batting to training in one change
// because the ask ("put training tab first") is about what a player LANDS
// on, not only left-to-right order: the nav item is "Training & Guides"
// (#184), which names training first, and training is the one tab you DO
// something on — a session is logged there. The other three are reference
// reading, so the thing you do goes ahead of the things you read.
//
// Every `?tab=` value still resolves, `?tab=batting` included. The explicit
// `?tab=training` links elsewhere in the app (both Dashboard "Log training"
// buttons, the coach nav item, `api/src/routes/activities.ts`,
// `lib/activityCreate.js`) become redundant but stay: an explicit tab is
// still correct, is self-documenting at the call site, and would silently
// re-point at whatever the default becomes next if it were dropped.
const DEFAULT_TAB = "training";

export default function LearningHubLegacy() {
  const [sp, setSp] = useSearchParams();
  const [openTopic, setOpenTopic] = useState(null);
  // Issue #480 — the Assigned tab is player-only; its trigger below is
  // gated on this. Role, not "has a player profile": the endpoint's
  // `resolveScope` (api/src/routes/authz.ts) branches on role before it
  // looks for a profile, so a coach who also holds a player profile still
  // gets nothing from this tab.
  const { user } = useAuth();
  const isPlayer = user?.role === "player";
  // Issue #1120: a player on the new look gets the kit bar and the ribbon
  // (LearnChrome) and the tabs strip leads the page; the hero card is dropped.
  const newLook = useLearnNewLook();
  // Issue #509 — the drill bank on the Training tab is gated to the two
  // roles the drills API admits as writers (`isCoachOrAdmin` in
  // api/src/routes/drills.ts). Role, not profile, same reasoning as
  // `isPlayer` above: the gate mirrors what the endpoint would answer.
  const isCoachOrAdmin = user?.role === "coach" || user?.role === "admin";
  // Issue #591 — the hero and the Training-tab prompt are role-aware here.
  // COACH-only, not `isCoachOrAdmin`: an admin reads the player-side surface
  // on this page (`/learn` is role-blind, Layout.jsx), so only a coach gets
  // the coach hero and the coach prompt; player and admin get the player copy.
  const isCoach = user?.role === "coach";
  // Issue #464: two readings of the same param. `chosenTab` is the tab the
  // player ASKED for — null on a bare `/learn` — and `tab` is the one this
  // page RENDERS, which needs the default. Only the first may clear
  // notifications; see the effect below.
  const chosenTab = sp.get("tab");
  const tab = chosenTab || DEFAULT_TAB;

  /**
   * Issue #417: landing on Training is "going to it on the platform" for a
   * coach-scheduled session (`coach_session`), the one bell type whose
   * destination is this page. Before this, a player who walked to the
   * session their coach put in their calendar kept the badge until they
   * opened the bell.
   *
   * Fires on every transition INTO a tab, not on every render — same shape
   * as `DevelopmentPlan.jsx`'s #184 stamp, and inert for every tab that is
   * not "training" because the surface table in `lib/notificationsRead.js`
   * maps those to nothing.
   *
   * ISSUE #464 — `chosenTab`, not `tab`. Training is the DEFAULT tab
   * (#218), so passing the defaulted value meant a bare `/learn` consumed
   * every `coach_session` item on arrival: the badge never counted a
   * session the player had not looked at. An absent `?tab=` clears nothing;
   * clicking the Training tab writes `?tab=training` and does clear.
   */
  useEffect(() => {
    markSurfaceNotificationsRead("learn", chosenTab);
  }, [chosenTab]);

  // Issues #219/#220 (epic #315), widened by #390: every reference tab is TWO
  // levels now — the three hand-written cards stay level 1 unchanged (PR #191,
  // Martin: "do not normalise the three concept cards into the guide schema —
  // there is no source document behind them to populate it"); the imported
  // guides are level 2, each rendered on its own page at
  // `/learn/:category/:slug` (GuidePage.jsx). Fetched once here, not per-tab.
  //
  // #219/#220 filtered to the batting category alone, because only batting
  // had been wired. #390's twenty-six technique guides (bowling 5,
  // facing-bowling 8, fielding 13) are already imported and reviewable, so
  // the filter moves to `surface` — the API's OWN answer to "which tab does
  // this guide belong on" (`CATEGORY_SURFACE` in `api/src/content/schema/guide.ts`:
  // facing bowling is a batting skill, field placement is captaincy the
  // fielding side does, mindset goes to Reflections). Deriving the tab here
  // from `category` instead would be a second copy of that mapping, free to
  // drift from the server's.
  //
  // #391 adds the Fitness tab (`surface: "fitness"`); #503 adds the Mindset
  // tab (`surface: "mindset"`, re-mapped off the never-built "reflections"
  // destination); #514 adds the Nutrition tab (`surface: "nutrition"`,
  // re-mapped off the fitness surface #391 folded it onto — fitness keeps
  // its own guides and drills). Grouping within a tab is by category, so
  // the eight facing-bowling guides read as their own labelled group under
  // Batting rather than being mixed into the twelve shot guides.
  //
  // Only PUBLISHED guides ever come back from this endpoint, so an unreviewed
  // guide is invisible here for every role — the same gate `learnGuides.ts`
  // enforces server-side, not a frontend filter.
  const [publishedGuides, setPublishedGuides] = useState([]);
  // Issue #517: this fetch used to swallow its failure into a permanently
  // empty guide list — every reference tab silently rendered as "no guides
  // here yet", a false claim of empty about a list that was never fetched
  // (the #271 class journeys 8d/8e forbid; #509/#511 fixed the same defect
  // in CoachPortal's picker, whose three-state idiom this mirrors). Three
  // states, because "empty" may only be claimed from "loaded".
  const [guidesStatus, setGuidesStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [guidesRetryNonce, setGuidesRetryNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setGuidesStatus("loading");
    fetchPublishedGuides()
      .then((guides) => {
        if (cancelled) return;
        setPublishedGuides(guides);
        setGuidesStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setGuidesStatus("failed");
      });
    return () => { cancelled = true; };
  }, [guidesRetryNonce]);

  /**
   * Issue #382 — the guides a coach has ASKED this player to read.
   *
   * A separate fetch from the published list above, and deliberately not a
   * filter over it: the assignment carries WHO asked and WHEN, which is the
   * whole point ("my coach asked me to read this" is a fact, not a guess —
   * the same attribution #190 gave a coach-set goal). Only published guides
   * ever come back, because the endpoint joins through the review rows; a
   * guide whose sign-off is later withdrawn drops out of this list on the
   * next load without the player being told a guide vanished.
   *
   * Issue #480 — the tab itself is PLAYER-ONLY (`isPlayer` above gates the
   * trigger): for a coach or admin this fetch would be a 4xx by
   * construction (`resolveScope` requires a `player_id` for those roles and
   * none is sent here), so the tab could never be anything but empty on
   * their screen, and Martin asked for it to go. Only the TRIGGER is gated
   * — a coach who deep-links `/learn?tab=assigned` still gets the harmless
   * empty panel (the pre-#480 status quo) rather than a blank page. A
   * coach's real view of assignments is Coach Portal → player → Guides.
   *
   * Issue #517 — a FAILED fetch no longer collapses into "Nothing assigned
   * yet": that is a claim about the player's data, and making it about a
   * list this page failed to fetch is the #271 false-empty class journeys
   * 8d/8e forbid. Three states, same idiom as CoachPortal's catalogue
   * fetch (#511): the empty card may only render from "loaded", and
   * "failed" renders a stated failure with a retry.
   *
   * The ROLE case and the network case are told apart BEFORE the request,
   * not by inspecting the error after it: for a non-player the 4xx is by
   * construction (above) — the client already knows the endpoint has
   * nothing for this role, exactly the reasoning that gates the trigger —
   * so no request is made and the state goes straight to "loaded" with the
   * empty list, keeping #480's deliberate quiet-empty panel. "failed" is
   * therefore only ever reachable for a role the endpoint actually serves,
   * which makes it mean what the note says: the network or server failed.
   */
  const [assigned, setAssigned] = useState([]);
  const [assignedStatus, setAssignedStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [assignedRetryNonce, setAssignedRetryNonce] = useState(0);
  useEffect(() => {
    if (!isPlayer) {
      setAssignedStatus("loaded");
      return undefined;
    }
    let cancelled = false;
    setAssignedStatus("loading");
    fetchAssignedGuides()
      .then((rows) => {
        if (cancelled) return;
        setAssigned(rows);
        setAssignedStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setAssignedStatus("failed");
      });
    return () => { cancelled = true; };
  }, [isPlayer, assignedRetryNonce]);

  /**
   * Issue #461 — the drills a coach has WRITTEN for this player, shown on
   * the same Assigned tab under the guides. Same posture as the guide
   * fetch above in every particular (#517 included): separate fetch (a
   * drill is a `coach_drills` row, not bundle content), the role case is
   * decided before the request while a real failure is stated with a
   * retry, and #480's player-only TRIGGER gating covers this list for
   * free because it lives inside the same panel.
   */
  const [assignedDrills, setAssignedDrills] = useState([]);
  const [drillsStatus, setDrillsStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [drillsRetryNonce, setDrillsRetryNonce] = useState(0);
  useEffect(() => {
    if (!isPlayer) {
      setDrillsStatus("loaded");
      return undefined;
    }
    let cancelled = false;
    setDrillsStatus("loading");
    fetchAssignedDrills()
      .then((rows) => {
        if (cancelled) return;
        setAssignedDrills(rows);
        setDrillsStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setDrillsStatus("failed");
      });
    return () => { cancelled = true; };
  }, [isPlayer, drillsRetryNonce]);

  return (
    <LearnChrome title="Training & guides">
    <div className="space-y-6" data-testid="learning-hub">
      {/* Hero */}
      {!newLook && (
      <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
        <div className="absolute inset-0 bg-grid-slate opacity-20" />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Cricket Coaching Hub</p>
            {/* #586 + #591: one role-aware hero, resolved in ONE place so the
                player copy (#586) and coach copy (#591) coexist instead of
                overwriting each other — the collision both tickets flagged.
                Player AND admin read the player surface; only a coach reads
                the coach surface (see `isCoach` above). Both H1s open with
                "Record a Training Session" because a bare /learn lands on the
                Training tab (DEFAULT_TAB, #218), the one tab you DO something
                on — the old "Coaching fundamentals" heading promised only
                reading. */}
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">Record a Training Session and Coaching fundamentals</h1>
            {isCoach ? (
              // #591: names the coach's tasks — recording a session, writing
              // drills into the bank, and reading the guides — and points at
              // where assignment lives. Assignment is My Players, not here
              // (#509): a subtitle promising "assign on this page" would
              // promise what the page cannot do, so it NAMES assignment and
              // points there (copy, not build — #509/#591, Andreas).
              <p className="text-white/70 mt-2 max-w-2xl">{`Record training sessions, write drills into your bank, and read the coaching guides for batting, bowling, fielding, wicketkeeping, field placement, fitness, nutrition and mindset. To assign guides or drills to a player, go to ${coachPlayersName()}.`}</p>
            ) : (
              // #586 (Martin): recording a session first, not "quick reference
              // guides" only. Names every reference tab including Wicketkeeping
              // (#585) so the subheader stops naming just three technique tabs.
              <p className="text-white/70 mt-2 max-w-2xl">Log your training sessions, and read the coaching guides for batting, bowling, fielding, wicketkeeping, field placement, fitness, nutrition and mindset.</p>
            )}
          </div>
          <BookOpen size={80} className="text-white/10 hidden md:block" />
        </div>
      </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => { const n = new URLSearchParams(sp); n.set("tab", v); setSp(n, { replace: true }); setOpenTopic(null); }}
        data-testid="learn-tabs"
      >
        <TabsList>
          <TabsTrigger value="training" data-testid="tab-training">Training</TabsTrigger>
          {/* #382. SECOND, not first: #218's note above ties the first tab
              to the tab a bare `/learn` LANDS on, and this change does not
              move that — a player who opens Training & Guides from the nav
              still gets Training. The bell is what points at this tab, and
              it does so by URL. The count is on the label because a tab
              whose whole job is "your coach asked for these" is worth
              nothing if you have to open it to find out there are none.
              #480: players only — for coach and admin the tab could never
              populate (see the fetch's comment above), so they don't get
              the trigger. The PANEL below stays ungated on purpose. */}
          {isPlayer && (
            <TabsTrigger value="assigned" data-testid="tab-assigned">
              {/* Guides + drills (#461): the count promises "things a coach
                  asked of you", and both lists live under this tab. */}
              Assigned{assigned.length + assignedDrills.length > 0 ? ` (${assigned.length + assignedDrills.length})` : ""}
            </TabsTrigger>
          )}
          <TabsTrigger value="batting" data-testid="tab-batting">Batting</TabsTrigger>
          <TabsTrigger value="bowling" data-testid="tab-bowling">Bowling</TabsTrigger>
          <TabsTrigger value="fielding" data-testid="tab-fielding">Fielding</TabsTrigger>
          {/* #585 (Martin, 2026-09-02): Wicketkeeping's own tab, beside
              Fielding where its ten guides used to be a labelled group. The
              re-map lives in CATEGORY_SURFACE (api/src/content/schema/guide.ts)
              and the guides' committed `surface`; this trigger and the TOPICS
              panel are the whole frontend change. Ninth trigger for a player
              (#480 hides Assigned from a coach, so eighth for them); the strip
              already scrolls within its own box at phone widths (#200's
              safe-center + overflow-x-auto), so it extends that scroll rather
              than wrapping. */}
          <TabsTrigger value="wicketkeeping" data-testid="tab-wicketkeeping">Wicketkeeping</TabsTrigger>
          {/* #105 (Martin, 2026-09-02): field placement's own trigger, after
              Wicketkeeping so the five on-field-skill tabs stay adjacent.
              Tenth trigger for a player (#480 hides Assigned from a coach,
              so ninth for them); the strip already scrolls within its own
              box at phone widths (#200's safe-center + overflow-x-auto), so
              this extends that scroll rather than wrapping the page. */}
          <TabsTrigger value="field-placement" data-testid="tab-field-placement">Field placement</TabsTrigger>
          {/* #391: the fourth advertised tile, live at last — fitness guides
              and drills land here by their API `surface` (nutrition rode
              this tab too until #514 gave it the trigger below). */}
          <TabsTrigger value="fitness" data-testid="tab-fitness">Fitness</TabsTrigger>
          {/* #514: the eight nutrition guides' own tab, beside Fitness where
              they used to be a labelled group. Eighth trigger for a player
              (#480 hides Assigned from a coach, so seventh for them); the
              strip already scrolls within its own box at phone widths
              (#200's safe-center + overflow-x-auto) — measured again at
              390×844 for this change, default and Large text. */}
          <TabsTrigger value="nutrition" data-testid="tab-nutrition">Nutrition</TabsTrigger>
          {/* #503: the twelve mindset guides' own tab — the "reflections"
              surface they used to carry had no reader anywhere. The TabsList
              already scrolls horizontally within its own box at phone
              widths (#200's safe-center + overflow-x-auto), so an added
              trigger extends that scroll rather than wrapping or forcing
              the page wide. */}
          <TabsTrigger value="mindset" data-testid="tab-mindset">Mindset</TabsTrigger>
        </TabsList>

        {/* Training panel first in source too, so reading this file gives the
            same order as the tab row. Only the selected panel is mounted
            (Radix `TabsContent` without `forceMount`), which is also why
            `/learn?new=1` with no tab now opens the training create dialog:
            `TrainingSessions` owns `useCreateDialog` (#79) and only exists
            in the DOM while this panel is the selected one. */}
        <TabsContent value="training" className="mt-6 space-y-4">
          {/* #591: this capture prompt is the PLAYER's — the only thing a
              player is asked to do on their Training tab — so it is GATED to
              players, not deleted. Deleting it would strip it off the player
              surface too, which #591 explicitly did not ask for. */}
          {isPlayer && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <ClipboardList size={14} color={NAVY} /> Capture lessons and reflections after every training session.
            </div>
          )}
          {/* #591: the coach's own prompt in its place. Drill AUTHORING lives
              here (the account-scoped bank below); ASSIGNING to a player does
              NOT — since #509 that is My Players → the player → Guides, because
              assignment needs a player and this page has none. So this names
              assignment and points there rather than building a picker here
              (copy, not build — #509/#591, Andreas). */}
          {isCoach && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <ClipboardList size={14} color={NAVY} /> {`Record a training session and write drills into your bank below. To assign guides or drills to a player, go to ${coachPlayersName()}.`}
            </div>
          )}
          <TrainingSessions />
          {/* Issue #509 — drill AUTHORING lives here, on the coach's
              Training home, as a bank: a drill is account-scoped, so no
              player needs selecting to write one. The per-player surface
              (Coach Portal → player → Guides) is assignment-only now. The
              panel owns its fetches and writes — this page only gates WHO
              sees it, and this function has a line budget the panel must
              not spend (eslint.config.mjs). */}
          {isCoachOrAdmin && <CoachDrillBankPanel />}
        </TabsContent>

        {/* Issue #382 — what a coach has asked this player to read.
            Menu-only, exactly like the level-2 guide list below: title,
            theme, and the one thing this list adds over that one — who
            asked, and when. The guide itself opens at
            `/learn/:category/:slug`, which applies the review gate again on
            the way in. */}
        <TabsContent value="assigned" className="mt-6 space-y-4" data-testid="assigned-panel">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <UserCheck size={14} color={GREEN} /> Guides your coach has asked you to read.
          </div>
          {/* #517: a failed fetch is stated as a failure with a retry —
              never rendered as the empty claim below, which would be a
              statement about a list this page failed to fetch. One note
              per loader: each names what is missing, and each retry
              re-runs only its own fetch. */}
          {assignedStatus === "failed" && (
            <LoadFailureNote
              what="your assigned guides"
              testId="assigned-guides-error"
              onRetry={() => setAssignedRetryNonce((n) => n + 1)}
            />
          )}
          {drillsStatus === "failed" && (
            <LoadFailureNote
              what="your assigned drills"
              testId="assigned-drills-error"
              onRetry={() => setDrillsRetryNonce((n) => n + 1)}
            />
          )}
          {/* "Nothing assigned yet" is a claim about BOTH lists, so it may
              only render once both have actually come back (#517). For a
              coach or admin both states are "loaded" without a request —
              the #480 quiet-empty panel, unchanged. */}
          {assignedStatus === "loaded" && drillsStatus === "loaded" && assigned.length === 0 && assignedDrills.length === 0 && (
            <Card className="border-slate-200" data-testid="assigned-empty">
              <div className="p-8 text-center">
                <BookOpen size={26} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600 text-sm">
                  Nothing assigned yet. Anything a coach asks you to read or work on will appear here.
                </p>
              </div>
            </Card>
          )}
          {assigned.length > 0 && (
            <div className="space-y-2">
              {assigned.map((a) => (
                <Link
                  key={a.assignment_id}
                  to={`/learn/${a.category}/${a.slug}`}
                  state={{ from: "/learn?tab=assigned" }}
                  data-testid={`assigned-guide-${a.category}-${a.slug}`}
                >
                  <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="p-4 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900">{a.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{a.key_theme}</div>
                        {/* The attribution — #382's acceptance criterion, and
                            the reason this is not just another guide link. */}
                        <div
                          className="text-[11px] mt-1.5 font-medium"
                          style={{ color: GREEN }}
                          data-testid={`assigned-by-${a.category}-${a.slug}`}
                        >
                          Assigned by {a.assigned_by_coach_name}
                          {fmtAssignedWhen(a.assigned_at) && ` · ${fmtAssignedWhen(a.assigned_at)}`}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-400 shrink-0" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          {/* Issue #461 — drills a coach wrote for this player, under the
              guides they assigned. The component renders nothing when the
              list is empty; the shared empty card above covers "neither". */}
          <AssignedDrillsList drills={assignedDrills} />
        </TabsContent>

        {Object.entries(TOPICS).map(([key, section]) => (
          <TabsContent key={key} value={key} className="mt-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Sparkles size={14} color={section.color} /> {section.tagline}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start" data-testid={`grid-${key}`}>
              {section.cards.map((card) => {
                const openKey = `${key}:${slugify(card.title)}`;
                const isOpen = openTopic === openKey;
                const triggerId = `qrg-trigger-${openKey}`;
                const panelId = `qrg-panel-${openKey}`;

                return (
                  <Card
                    key={card.title}
                    data-state={isOpen ? "open" : "closed"}
                    className={cn(
                      "border-slate-200 transition-all overflow-hidden",
                      isOpen ? "shadow-md" : "hover:shadow-md hover:-translate-y-0.5",
                      // Issue #458 (Martin, 2026-08-23): "QRGs don't render
                      // full screen on large screens." A closed card is one
                      // tile of a 3-up menu, which is right; an OPEN one was
                      // still one tile, so from `md:` up the guide itself —
                      // purpose plus every coaching point — was read in a
                      // ~1/3-width column while two thirds of the screen sat
                      // empty. The narrower the column, the worse it got,
                      // because the panel is the only content on the page a
                      // player actually reads rather than scans.
                      //
                      // Spanning the whole row when open is the smallest fix
                      // that uses the screen AND keeps the disclosure shape
                      // #96 established (a real <button> with
                      // aria-expanded/aria-controls, expanding in place). A
                      // dialog would have used the screen too and cost both:
                      // the harness reaches this panel through
                      // TAB_EXPAND_TRIGGERS (`topic-*` in
                      // e2e/dialog-config.js), which measures expand-in-place
                      // disclosures, not `[role="dialog"]`.
                      //
                      // Grid auto-placement can leave a hole in the row above
                      // an opened card. That is the deliberate trade: card
                      // ORDER is preserved (the menu still reads the way it
                      // is written), and only one card is ever open, so at
                      // most one row is affected.
                      isOpen && "md:col-span-3"
                    )}
                    style={isOpen ? { borderColor: section.color, borderWidth: 2, background: `${section.color}05` } : undefined}
                  >
                    {/* The interactive control itself — a real <button>, not a
                        clickable <div>, so Tab/Enter/Space and the focus ring
                        all come for free rather than being hand-rolled. The
                        testid lives here (not on the outer Card) because this
                        is the element tests should actually click. */}
                    <button
                      type="button"
                      id={triggerId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenTopic(isOpen ? null : openKey)}
                      data-testid={`topic-${card.title}`}
                      // The outer Card clips to rounded-xl (overflow-hidden,
                      // below) and this button fills its content box flush
                      // to every edge, so a ring-OFFSET focus style would be
                      // cropped away by that clip — invisible exactly where
                      // #96 requires a visible focus ring. An inset ring
                      // draws inside the button's own box instead, so it
                      // survives the clip.
                      className="w-full text-left bg-transparent border-0 p-5 cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${section.color}15` }}>
                          <BookOpen size={18} color={section.color} />
                        </div>
                        <ChevronDown
                          size={16}
                          className={cn("mt-1.5 text-slate-400 transition-transform flex-shrink-0", isOpen && "rotate-180")}
                          aria-hidden="true"
                        />
                      </div>
                      <div className="mt-3 font-display font-bold text-slate-900">{card.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{card.purpose}</div>
                    </button>

                    {isOpen && (
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={triggerId}
                        data-testid="qrg-content"
                        // Two columns from `lg:` up (#458). Width alone is not
                        // the fix: a full-bleed single column at 1440px sets
                        // coaching points on ~1300px lines, which is worse to
                        // read than the narrow column it replaced. Purpose
                        // beside the points keeps both at a readable measure
                        // and puts the whole guide on one screen without
                        // scrolling. Below `lg:` the card is not spanning
                        // either, so this stays the stacked layout it was.
                        className="px-5 pb-5 pt-1 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start border-t border-slate-100"
                      >
                        <div className="rounded-xl p-4 border-l-4" style={{ background: `${section.color}0A`, borderColor: section.color }}>
                          <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: section.color }}>Purpose</div>
                          <p className="text-sm text-slate-800">{card.purpose}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <ListChecks size={14} color={section.color} />
                            <div className="text-[11px] uppercase tracking-widest text-slate-500">Key coaching points</div>
                          </div>
                          <ul className="space-y-1.5">
                            {card.points.map((p, i) => (
                              <li key={i} className="text-sm text-slate-700 flex gap-2">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: section.color }} />{p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* #517: a failed guide-list fetch is stated as a failure with
                a retry — never rendered as the silent "no guides on this
                tab" the groups below collapse to, which would be a claim
                about a list this page failed to fetch (the #271 false-empty
                class). One render site, but only the selected panel is
                mounted, so exactly one note is ever on screen. */}
            {guidesStatus === "failed" && (
              <LoadFailureNote
                what="the coaching guides"
                testId="learn-guides-error"
                onRetry={() => setGuidesRetryNonce((n) => n + 1)}
              />
            )}

            {/* Level 2 (issues #219/#220, widened by #390): the imported
                guides, menu-only per spec §2.1 point 2 — title + key_theme
                subtitle, no detail here. One group per category that the
                API placed on this tab's surface; an empty group renders
                nothing, so a tab whose guides are all still unreviewed looks
                exactly as it does today. Nothing here can show an unreviewed
                guide: `fetchPublishedGuides` reads the gated endpoint, which
                is driven from the `guide_review_state` rows. */}
            {GUIDE_GROUPS.map(({ category, label }) => {
              const groupGuides = publishedGuides.filter(
                (g) => g.surface === key && g.category === category
              );
              if (groupGuides.length === 0) return null;
              return (
                <div key={category} data-testid={`guide-group-${category}`}>
                  {/* #503 defect 2 ("nutrition has no header of its own"):
                      the header was always here — browser-verified against
                      #501's build — but at 11px slate-500 it vanished
                      between two walls of 17px-bold card titles, so the
                      nutrition guides read as the tail of the fitness list.
                      Legibility, not structure: bolder/darker label, a
                      touch more headroom, and a hairline rule filling the
                      row so every group header reads as a section break.
                      One render site, so every group on every tab gets the
                      same treatment. */}
                  <div className="flex items-center gap-2 mb-2.5 mt-4">
                    <BookOpen size={16} color={section.color} />
                    <div className="text-[13px] uppercase tracking-widest font-bold text-slate-700 whitespace-nowrap">{label}</div>
                    <div className="h-px flex-1 bg-slate-200" aria-hidden="true" />
                  </div>
                  <div className="space-y-2">
                    {groupGuides.map((g) => (
                      // Category-qualified key and testid, not slug alone: the
                      // corpus has `leg-spin` and `off-spin` in BOTH bowling
                      // (bowling it) and facing-bowling (playing it), so a
                      // slug-only key is a real collision, not a hypothetical.
                      <Link
                        key={`${g.category}/${g.slug}`}
                        to={`/learn/${g.category}/${g.slug}`}
                        // #1125: the guide's Back returns to THIS tab (`key`
                        // is the tab, the surface the API placed the guide
                        // on), not to whatever the hub lands on by default.
                        state={{ from: `/learn?tab=${key}` }}
                        data-testid={`guide-link-${g.category}-${g.slug}`}
                      >
                        <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all">
                          <div className="p-4 flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-slate-900">{g.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5 truncate">{g.key_theme}</div>
                            </div>
                            <ChevronRight size={16} className="text-slate-400 shrink-0" />
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>
        ))}
      </Tabs>
    </div>
    </LearnChrome>
  );
}
